// BotContainer.tsx - FIXED: CREATE mode now uses same 2-tab system as EDIT
// 🎯 MAJOR FIX: 
// - CREATE mode now properly split into 2 tabs like EDIT mode
// - Consistent UX between create and edit experiences  
// - Proper form section control using BotForm props
// - Fixed TypeScript errors with BotList component props

import React, { useState, useEffect } from 'react';
import Header from "../../components/header";
import BotForm from './BotForm';
import BotList from './BotList';
import BotMetrics from './BotMetrics';
import ErrorBoundary from '../../components/ErrorBoundary';
import UltimatePlanInfo from '../../components/UltimatePlanInfo/UltimatePlanInfo';
import BackToTop from './components/BackToTop';
// Removed Container import - using custom full-width containers
import logger from '../../utils/logger';

// 🎯 TYPES: View and tab management
type ViewMode = 'list' | 'create' | 'edit' | 'metrics';
type EditTabMode = 'basic' | 'questions'; // Used for both CREATE and EDIT

const BotContainer: React.FC = () => {
  logger.log('🏠 BotContainer: Component rendered - FIXED CREATE MODE VERSION');

  // 🔥 CORE STATE: View management
  const [currentView, setCurrentView] = useState<ViewMode>('list');
  const [editingBotId, setEditingBotId] = useState<string | null>(null);
  
  // 🎯 UNIFIED: Tab state used for BOTH create and edit modes
  const [currentTab, setCurrentTab] = useState<EditTabMode>('basic');
  
  // 🔥 FORM STATE: Tracking for container-level actions
  const [isFormValid, setIsFormValid] = useState(true);
  const [isFormSaving, setIsFormSaving] = useState(false);

  // 🔥 PLAN STATE: Ultimate plan check
  const [isUltimate, setIsUltimate] = useState<boolean | null>(null);

  logger.log('📊 BotContainer: Current state:', { 
    currentView, 
    editingBotId, 
    currentTab, // 🎯 NOW: Used for both create AND edit modes
    isFormValid,
    isFormSaving,
    isUltimate
  });

  // 🔥 Ultimate plan check
  useEffect(() => {
    try {
      logger.log('🔍 BotContainer: Checking ultimate plan status');
      const localData = window.localStorage?.getItem("userData");
      if (localData) {
        const userData = JSON.parse(localData);
        const planType = userData?.subscriptionPlanType;
        const isUltimatePlan = typeof planType === 'string' && planType.toLowerCase() === "ultimate";
        
        logger.log('📋 BotContainer: Plan check result:', { 
          planType, 
          isUltimatePlan,
          userDataExists: !!userData 
        });
        
        setIsUltimate(isUltimatePlan);
      } else {
        logger.warn('❌ BotContainer: No userData found in localStorage');
        setIsUltimate(false);
      }
    } catch (error) {
      console.error('❌ BotContainer: Error checking ultimate plan:', error);
      setIsUltimate(false);
    }
  }, []);

  // 🔥 NAVIGATION HANDLERS
  
  // Handle navigation to create new bot
  const handleCreateNew = () => {
    logger.log('➕ BotContainer: Navigating to create new bot');
    setCurrentView('create');
    setEditingBotId(null);
    setCurrentTab('basic'); // 🎯 FIXED: Always start with basic tab
    setIsFormValid(true);
    setIsFormSaving(false);
  };

  // Handle navigation to edit existing bot
  const handleEditBot = (botId: string) => {
    logger.log('✏️ BotContainer: Navigating to edit bot:', botId);
    setCurrentView('edit');
    setEditingBotId(botId);
    setCurrentTab('basic'); // Always start with basic tab
    setIsFormValid(true);
    setIsFormSaving(false);
  };

  // Handle successful form submission
  const handleFormSuccess = () => {
    logger.log('✅ BotContainer: Form submitted successfully, returning to list');
    setCurrentView('list');
    setEditingBotId(null);
    setCurrentTab('basic');
    setIsFormValid(true);
    setIsFormSaving(false);
  };

  // Handle form cancellation
  const handleFormCancel = () => {
    logger.log('❌ BotContainer: Form cancelled, returning to list');
    setCurrentView('list');
    setEditingBotId(null);
    setCurrentTab('basic');
    setIsFormValid(true);
    setIsFormSaving(false);
  };

  // 🎯 Tab switching handler - Used for BOTH create and edit
  const handleTabSwitch = (tab: EditTabMode) => {
    logger.log('🔄 BotContainer: Switching to tab:', tab, 'in mode:', currentView);
    setCurrentTab(tab);
  };

  // 🔥 Navigate to metrics (separate view, not tab)
  const handleViewMetrics = () => {
    logger.log('📊 BotContainer: Navigating to metrics view');
    setCurrentView('metrics');
  };

  // Handle form state updates from BotForm component
  const handleFormStateChange = (isValid: boolean, isSaving: boolean) => {
    logger.log('📊 BotContainer: Form state changed:', { isValid, isSaving });
    setIsFormValid(isValid);
    setIsFormSaving(isSaving);
  };

  // Handle form submission from container level
  const handleContainerFormSubmit = () => {
    logger.log('🚀 BotContainer: Triggering form submission from container');
    
    const form = document.getElementById('botForm') as HTMLFormElement;
    if (form) {
      const submitEvent = new Event('submit', { 
        cancelable: true, 
        bubbles: true 
      });
      form.dispatchEvent(submitEvent);
    } else {
      console.error('❌ BotContainer: Form element not found');
    }
  };

  // 🔥 RENDER LOGIC: Main view rendering with UNIFIED tab system

  const renderCurrentView = () => {
    logger.log('🔄 BotContainer: Rendering view:', currentView);

    switch (currentView) {
      
      // 📝 LIST MODE: Standard bot list
      // Note: Metrics can be accessed by editing a bot and clicking the Analytics button
      case 'list':
        logger.log('📋 BotContainer: Rendering bot list');
        return (
          <BotList 
            onCreateNew={handleCreateNew}
            onEditBot={handleEditBot}
          />
        );

      // 📝 CREATE MODE: NOW USES SAME 2-TAB SYSTEM AS EDIT! 🎯
      case 'create':
        logger.log('📝 BotContainer: Rendering create form with 2-tab system - FIXED!');
        return (
          <div id="main" className="main">
            <Header />
            <div style={{ width: '100%', padding: '24px', boxSizing: 'border-box' }}>
              
              {/* 🔥 CREATE MODE: Header with Title, Actions AND Tab Navigation */}
              <div style={{ 
                background: 'white', 
                borderRadius: '12px', 
                marginBottom: '24px', 
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                overflow: 'hidden'
              }}>
                
                {/* Top Section: Title and Actions */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  padding: '32px 40px 24px 40px',
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  
                  {/* Left: Title and Description */}
                  <div>
                    <h1 style={{
                      fontFamily: 'Gilroy, sans-serif',
                      fontSize: '36px',
                      fontWeight: '700',
                      color: '#111827',
                      margin: '0 0 8px 0',
                      letterSpacing: '-0.025em'
                    }}>
                      Create New Bot
                    </h1>
                    <p style={{
                      fontFamily: 'Gilroy, sans-serif',
                      fontSize: '16px',
                      fontWeight: '500',
                      color: '#6b7280',
                      margin: '0'
                    }}>
                      Build your WhatsApp bot step by step - templates are now question types!
                    </p>
                  </div>
                  
                  {/* Right: Actions */}
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    
                    {/* Cancel Button */}
                    <button
                      onClick={handleFormCancel}
                      style={{
                        background: 'transparent',
                        color: '#6b7280',
                        border: '2px solid #e5e7eb',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        fontFamily: 'Gilroy, sans-serif',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '14px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.color = '#374151';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.color = '#6b7280';
                      }}
                    >
                      Cancel
                    </button>
                    
                    {/* Create Button */}
                    <button
                      onClick={handleContainerFormSubmit}
                      disabled={!isFormValid || isFormSaving}
                      style={{
                        background: (!isFormValid || isFormSaving) 
                          ? '#d1d5db' 
                          : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        fontFamily: 'Gilroy, sans-serif',
                        fontWeight: '600',
                        cursor: (!isFormValid || isFormSaving) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        minWidth: '140px',
                        fontSize: '14px',
                        boxShadow: (!isFormValid || isFormSaving) ? 'none' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        opacity: (!isFormValid || isFormSaving) ? 0.6 : 1
                      }}
                    >
                      {isFormSaving ? (
                        <>
                          <span style={{ marginRight: '8px' }}>⏳</span>
                          Creating...
                        </>
                      ) : (
                        'Create Bot'
                      )}
                    </button>
                  </div>
                </div>

                {/* 🎯 FIXED: 2-Tab Navigation for CREATE mode */}
                <div style={{ 
                  display: 'flex', 
                  padding: '0 32px',
                  background: '#f9fafb'
                }}>
                  
                  {/* 📝 BASIC SETUP TAB */}
                  <button
                    onClick={() => handleTabSwitch('basic')}
                    style={{
                      padding: '16px 24px',
                      border: 'none',
                      background: 'transparent',
                      fontFamily: 'Gilroy, sans-serif',
                      fontWeight: '700',
                      fontSize: '16px',
                      cursor: 'pointer',
                      borderBottom: currentTab === 'basic' ? '3px solid #3FAEFD' : '3px solid transparent',
                      color: currentTab === 'basic' ? '#3FAEFD' : '#6b7280',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      if (currentTab !== 'basic') {
                        e.currentTarget.style.color = '#374151';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentTab !== 'basic') {
                        e.currentTarget.style.color = '#6b7280';
                      }
                    }}
                  >
                    📝 Basic Setup
                  </button>
                  
                  {/* 🎯 QUESTIONS TAB */}
                  <button
                    onClick={() => handleTabSwitch('questions')}
                    style={{
                      padding: '16px 24px',
                      border: 'none',
                      background: 'transparent',
                      fontFamily: 'Gilroy, sans-serif',
                      fontWeight: '700',
                      fontSize: '16px',
                      cursor: 'pointer',
                      borderBottom: currentTab === 'questions' ? '3px solid #3FAEFD' : '3px solid transparent',
                      color: currentTab === 'questions' ? '#3FAEFD' : '#6b7280',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      if (currentTab !== 'questions') {
                        e.currentTarget.style.color = '#374151';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentTab !== 'questions') {
                        e.currentTarget.style.color = '#6b7280';
                      }
                    }}
                  >
                    🎯 Questions
                  </button>
                </div>
              </div>

              {/* 🎯 FIXED: 2-Tab Content Rendering for CREATE mode */}
              
             {/* 🔥 FIXED: Single BotForm with Error Boundary - no more unmounting */}
             <ErrorBoundary componentName="BotForm (Create Mode)">
<BotForm
  isEdit={false}
  onSuccess={handleFormSuccess}
  onCancel={handleFormCancel}
  hideHeader={true}
  onFormStateChange={handleFormStateChange}
  showOnlyBasicSections={currentTab === 'basic'}
  showOnlyQuestionsSection={currentTab === 'questions'}
  enableTemplateQuestions={true}
  hideResponseType={true}
/>
             </ErrorBoundary>

              {/* 🔥 REMOVED: Duplicate BotForm - the first BotForm handles tab switching */}

            </div>
          </div>
        );

      // ✏️ EDIT MODE: Same 2-tab system as before (unchanged)
      case 'edit':
        logger.log('✏️ BotContainer: Rendering edit mode with 2-tab system');
        
        if (!editingBotId) {
          console.error('❌ BotContainer: Edit mode but no botId provided');
          setCurrentView('list');
          return null;
        }
        
        return (
          <div id="main" className="main">
            <Header />
            <div style={{ width: '100%', padding: '24px', boxSizing: 'border-box' }}>
              
              {/* 🔥 EDIT MODE: Header with Title, Actions AND Tab Navigation */}
              <div style={{ 
                background: 'white', 
                borderRadius: '12px', 
                marginBottom: '24px', 
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                overflow: 'hidden'
              }}>
                
                {/* Top Section: Title and Actions */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  padding: '32px 40px 24px 40px',
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  
                  {/* Left: Title and Description */}
                  <div>
                    <h1 style={{
                      fontFamily: 'Gilroy, sans-serif',
                      fontSize: '36px',
                      fontWeight: '700',
                      color: '#111827',
                      margin: '0 0 8px 0',
                      letterSpacing: '-0.025em'
                    }}>
                      Edit Bot
                    </h1>
                    <p style={{
                      fontFamily: 'Gilroy, sans-serif',
                      fontSize: '16px',
                      fontWeight: '500',
                      color: '#6b7280',
                      margin: '0'
                    }}>
                      Modify your WhatsApp bot - templates are now question types!
                    </p>
                  </div>
                  
                  {/* Right: Actions */}
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    
                    {/* View Analytics Button */}
                    <button
                      onClick={handleViewMetrics}
                      style={{
                        background: 'transparent',
                        color: '#6b7280',
                        border: '2px solid #e5e7eb',
                        padding: '12px 20px',
                        borderRadius: '8px',
                        fontFamily: 'Gilroy, sans-serif',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.color = '#374151';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.color = '#6b7280';
                      }}
                    >
                      📊 Analytics
                    </button>
                    
                    {/* Back to List Button */}
                    <button
                      onClick={handleFormCancel}
                      style={{
                        background: 'transparent',
                        color: '#6b7280',
                        border: '2px solid #e5e7eb',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        fontFamily: 'Gilroy, sans-serif',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '14px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.color = '#374151';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.color = '#6b7280';
                      }}
                    >
                      ← Back to List
                    </button>
                    
                    {/* Update Button */}
                    <button
                      onClick={handleContainerFormSubmit}
                      disabled={!isFormValid || isFormSaving}
                      style={{
                        background: (!isFormValid || isFormSaving) 
                          ? '#d1d5db' 
                          : 'linear-gradient(135deg, #3FAEFD 0%, #2563eb 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        fontFamily: 'Gilroy, sans-serif',
                        fontWeight: '600',
                        cursor: (!isFormValid || isFormSaving) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        minWidth: '160px',
                        fontSize: '14px',
                        boxShadow: (!isFormValid || isFormSaving) ? 'none' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        opacity: (!isFormValid || isFormSaving) ? 0.6 : 1
                      }}
                    >
                      {isFormSaving ? (
                        <>
                          <span style={{ marginRight: '8px' }}>⏳</span>
                          Updating...
                        </>
                      ) : (
                        'Update Bot'
                      )}
                    </button>
                  </div>
                </div>

                {/* 🎯 2-Tab Navigation for EDIT mode (same as CREATE) */}
                <div style={{ 
                  display: 'flex', 
                  padding: '0 32px',
                  background: '#f9fafb'
                }}>
                  
                  {/* 📝 BASIC SETUP TAB */}
                  <button
                    onClick={() => handleTabSwitch('basic')}
                    style={{
                      padding: '16px 24px',
                      border: 'none',
                      background: 'transparent',
                      fontFamily: 'Gilroy, sans-serif',
                      fontWeight: '700',
                      fontSize: '16px',
                      cursor: 'pointer',
                      borderBottom: currentTab === 'basic' ? '3px solid #3FAEFD' : '3px solid transparent',
                      color: currentTab === 'basic' ? '#3FAEFD' : '#6b7280',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      if (currentTab !== 'basic') {
                        e.currentTarget.style.color = '#374151';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentTab !== 'basic') {
                        e.currentTarget.style.color = '#6b7280';
                      }
                    }}
                  >
                    📝 Basic Setup
                  </button>
                  
                  {/* 🎯 QUESTIONS TAB */}
                  <button
                    onClick={() => handleTabSwitch('questions')}
                    style={{
                      padding: '16px 24px',
                      border: 'none',
                      background: 'transparent',
                      fontFamily: 'Gilroy, sans-serif',
                      fontWeight: '700',
                      fontSize: '16px',
                      cursor: 'pointer',
                      borderBottom: currentTab === 'questions' ? '3px solid #3FAEFD' : '3px solid transparent',
                      color: currentTab === 'questions' ? '#3FAEFD' : '#6b7280',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      if (currentTab !== 'questions') {
                        e.currentTarget.style.color = '#374151';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentTab !== 'questions') {
                        e.currentTarget.style.color = '#6b7280';
                      }
                    }}
                  >
                    🎯 Questions
                  </button>
                </div>
              </div>

              {/* 🎯 2-Tab Content Rendering for EDIT mode */}
              
            {/* 🔥 FIXED: Single BotForm with Error Boundary - no more unmounting */}
            <ErrorBoundary componentName="BotForm (Edit Mode)">
<BotForm
  isEdit={true}
  botId={editingBotId}
  onSuccess={handleFormSuccess}
  onCancel={handleFormCancel}
  hideHeader={true}
  onFormStateChange={handleFormStateChange}
  showOnlyBasicSections={currentTab === 'basic'}
  showOnlyQuestionsSection={currentTab === 'questions'}
  enableTemplateQuestions={true}
  hideResponseType={true}
/>
            </ErrorBoundary>

              {/* 🎯 QUESTIONS TAB CONTENT - EDIT MODE */}
              {/* 🔥 REMOVED: Duplicate BotForm - the first BotForm handles tab switching */}

            </div>
          </div>
        );

      // 📊 METRICS MODE: Separate metrics view (unchanged)
      case 'metrics':
        logger.log('📊 BotContainer: Rendering metrics view');
        if (!editingBotId) {
          setCurrentView('list');
          return null;
        }
        return (
          <div id="main" className="main">
            <Header />
            <div style={{ width: '100%', padding: '24px', boxSizing: 'border-box' }}>
              <div style={{
                background: 'white',
                borderRadius: '12px',
                marginBottom: '24px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                overflow: 'hidden'
              }}>
                {/* Metrics Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '24px 32px',
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <div>
                    <h1 style={{
                      fontFamily: 'Gilroy, sans-serif',
                      fontSize: '28px',
                      fontWeight: '700',
                      color: '#111827',
                      margin: '0 0 8px 0'
                    }}>
                      📊 Bot Analytics
                    </h1>
                    <p style={{
                      fontFamily: 'Gilroy, sans-serif',
                      fontSize: '14px',
                      color: '#6b7280',
                      margin: '0'
                    }}>
                      Performance metrics and session history
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setCurrentView('edit');
                      setCurrentTab('basic');
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #3FAEFD 0%, #2563eb 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontFamily: 'Gilroy, sans-serif',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    ← Back to Edit
                  </button>
                </div>
              </div>
              <ErrorBoundary componentName="BotMetrics">
                <BotMetrics botId={editingBotId} />
              </ErrorBoundary>
            </div>
          </div>
        );

      // 🏠 DEFAULT: Back to list (fallback)
      default:
        logger.warn('❌ BotContainer: Unknown view mode:', currentView, '- defaulting to list');
        setCurrentView('list');
        return null;
    }
  };

  // 🔥 ULTIMATE PLAN CHECK: Show upgrade notice if needed
  if (isUltimate === false) {
    logger.log('🔒 BotContainer: Showing Ultimate plan upgrade notice');
    return <UltimatePlanInfo />;
  }

  // 🔄 LOADING STATE: While checking plan
  if (isUltimate === null) {
    logger.log('⏳ BotContainer: Checking plan status...');
    return (
      <div id="main" className="main">
        <Header />
        <div style={{ width: '100%', padding: '24px', boxSizing: 'border-box', textAlign: 'center' }}>
          <div style={{ padding: '60px 0' }}>
            <div style={{ 
              display: 'inline-block',
              width: '40px', 
              height: '40px', 
              border: '4px solid #f3f4f6',
              borderTop: '4px solid #3FAEFD',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ 
              marginTop: '20px', 
              color: '#6b7280',
              fontFamily: 'Gilroy, sans-serif' 
            }}>
              Loading...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 🎨 MAIN RENDER
  logger.log('🎨 BotContainer: Rendering main view:', currentView);
  return (
    <>
      {renderCurrentView()}
      {/* Show BackToTop button only when in form views */}
      {(currentView === 'create' || currentView === 'edit') && <BackToTop />}
    </>
  );
};

export default BotContainer;

// 🎯 SUMMARY OF CHANGES:
// 1. ✅ CREATE mode now uses same 2-tab system as EDIT mode
// 2. ✅ Consistent UX between create and edit experiences  
// 3. ✅ Proper form section control using BotForm props
// 4. ✅ handleTabSwitch works for both CREATE and EDIT modes
// 5. ✅ Form validation and saving state properly managed
// 6. ✅ Console logging for debugging throughout
// 7. ✅ Comments explaining the logic at each step
// 8. ✅ Fixed TypeScript errors with BotList component props